import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import Select from 'react-select';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import Swal from 'sweetalert2';
import { BASE_URL, fetchSalesPersonVisitEntryReport, fetchUsers, insertSalesPersonVisit } from 'Service/ApiService';

const APP_RED = '#e31e24';
const APP_DARK = '#121212';
const BRAND_RED = '#7a2a24';

const brandOptionSx = {
  m: 0,
  px: 1.25,
  py: 0.25,
  border: '1px solid #e6ddcc',
  borderRadius: '20px',
  bgcolor: '#fbf9f5',
  '& .MuiCheckbox-root': {
    p: 0.5,
    mr: 0.25,
    color: '#7b7b7b',
    '&.Mui-checked': { color: BRAND_RED }
  },
  '& .MuiFormControlLabel-label': {
    color: `${APP_DARK} !important`,
    fontSize: 12.5,
    fontWeight: 500
  }
};

const radioIcon = <Box sx={{ width: 18, height: 18, border: `2px solid ${APP_DARK}`, borderRadius: '50%', bgcolor: '#fff' }} />;
const radioCheckedIcon = (
  <Box sx={{ width: 18, height: 18, border: `2px solid ${APP_RED}`, borderRadius: '50%', bgcolor: '#fff', display: 'grid', placeItems: 'center' }}>
    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: APP_RED }} />
  </Box>
);

const sections = [
  {
    title: 'Visit & Sales Person Details',
    fields: [
      { name: 'sales_person', label: 'SSML Secondary Sales Person Name', placeholder: 'e.g. Amit Kumar Sharma', required: true, xs: 12, sm: 6 },
      { name: 'location', label: 'Location / Address', placeholder: 'e.g. Tatanagar', xs: 12, sm: 6 },
      { name: 'pincode', label: 'Pincode', placeholder: '6-digit pincode', type: 'tel', inputProps: { pattern: '[0-9]{6}', maxLength: 6 }, xs: 12, sm: 6 }
    ]
  },
  {
    title: 'Retailer Details',
    fields: [
      { name: 'retailer_name', label: 'Retailer Name', placeholder: 'e.g. Mahabir Cloth Store', required: true },
      { name: 'place', label: 'Place', placeholder: 'e.g. Jugsalai' },
      { name: 'proprietor', label: 'Proprietor Name', placeholder: 'e.g. Rakesh Kumar Jain' },
      { name: 'mobile', label: 'Mobile No.', placeholder: '10-digit mobile number', type: 'tel', inputProps: { pattern: '[0-9]{10}', maxLength: 10 }, helperText: 'Enter a valid 10-digit mobile number' },
      { name: 'proprietor_email', label: 'Proprietor Email ID', placeholder: 'name@example.com', type: 'email' },
      { name: 'retailer_email', label: 'Retailer Email ID', placeholder: 'name@example.com', type: 'email' },
      { name: 'dob', label: 'Proprietor Date of Birth', type: 'date', inputLabelProps: { shrink: true } },
      { name: 'shop_area', label: 'Retailer Shop Outlet Area', placeholder: 'e.g. 500 Sq. Ft.' }
    ]
  }
];

const stockGroups = [
  { title: 'Total Stock in the Shop', fields: [['stock_total_shirting', 'Shirting (pcs)'], ['stock_total_suiting', 'Suiting (pcs)']] },
  { title: 'Total Shop Sale', fields: [['sale_daily', 'Daily'], ['sale_monthly', 'Monthly'], ['sale_yearly', 'Yearly'], ['sample_mill_dealer', 'Total Sample of Mill & Dealer']] },
  { title: 'Total SSML Brand Stock Displayed in the Shop', fields: [['ssml_stock_shirting', 'Shirting (pcs)'], ['ssml_stock_suiting', 'Suiting (pcs)']] },
  { title: 'Total Sale of SSML Brand', fields: [['ssml_sale_shirting', 'Shirting (pcs)'], ['ssml_sale_suiting', 'Suiting (pcs)']] },
  { title: 'Total White Thaan Stock in the Shop', fields: [['white_thaan_shop_shirting', 'Shirting (pcs)'], ['white_thaan_shop_suiting', 'Suiting (pcs)']] },
  { title: 'Total SSML White Thaan Stock in the Shop', fields: [['white_thaan_ssml_shirting', 'Shirting (pcs)'], ['white_thaan_ssml_suiting', 'Suiting (pcs)']] },
  { title: 'Total Black Thaan Stock in the Shop', fields: [['black_thaan_shop_shirting', 'Shirting (pcs)'], ['black_thaan_shop_suiting', 'Suiting (pcs)']] },
  { title: 'Total SSML Black Thaan Stock in the Shop', fields: [['black_thaan_ssml_shirting', 'Shirting (pcs)'], ['black_thaan_ssml_suiting', 'Suiting (pcs)']] }
];

const visitTableGroups = [
  { label: 'SSML Secondary Sales Person Name', fields: [['sales_person', '']] },
  { label: 'Location', fields: [['location', '']] },
  { label: 'Pincode', fields: [['pincode', '']] },
  { label: 'Retailer Name', fields: [['retailer_name', '']] },
  { label: 'Place', fields: [['place', '']] },
  { label: 'Proprietor', fields: [['proprietor', '']] },
  { label: 'Mobile No.', fields: [['mobile', '']] },
  { label: 'Proprietor Email ID', fields: [['proprietor_email', '']] },
  { label: 'Retailer Email ID', fields: [['retailer_email', '']] },
  { label: 'DOB', fields: [['dob', '']] },
  { label: 'Retailer Shop Outlet Area', fields: [['shop_area', '']] },
  { label: 'Total Stock in the shop', fields: [['stock_total_shirting', 'Shirting'], ['stock_total_suiting', 'Suiting']] },
  { label: 'Total Shop Sale', fields: [['sale_daily', 'Daily'], ['sale_monthly', 'Monthly'], ['sale_yearly', 'Yearly']] },
  { label: 'Total Sample of Mill & Dealer', fields: [['sample_mill_dealer', '']] },
  { label: 'Total SSML Brand Stock Displayed in the Shop', fields: [['ssml_stock_shirting', 'Shirting'], ['ssml_stock_suiting', 'Suiting']] },
  { label: 'Total Sale of SSML Brand', fields: [['ssml_sale_shirting', 'Shirting'], ['ssml_sale_suiting', 'Suiting']] },
  { label: 'Total White Thaan Stock in the Shop', fields: [['white_thaan_shop_shirting', 'Shirting'], ['white_thaan_shop_suiting', 'Suiting']] },
  { label: 'Total SSML White Thaan Stock in the Shop', fields: [['white_thaan_ssml_shirting', 'Shirting'], ['white_thaan_ssml_suiting', 'Suiting']] },
  { label: 'Total Black Thaan Stock in the Shop', fields: [['black_thaan_shop_shirting', 'Shirting'], ['black_thaan_shop_suiting', 'Suiting']] },
  { label: 'Total SSML Black Thaan Stock in the shop', fields: [['black_thaan_ssml_shirting', 'Shirting'], ['black_thaan_ssml_suiting', 'Suiting']] },
  { label: 'Colour Solidness & Sample Cutting', fields: [['colour_solidness', '']] },
  { label: 'Dealer Repo Feedback', fields: [['dealer_repo_feedback', '']] }
];
const visitTableColumns = visitTableGroups.flatMap(({ fields }) => fields);

const Field = ({ field, resetVersion }) => {
  const [error, setError] = useState('');
  const isMobile = field.name === 'mobile';
  const isPincode = field.name === 'pincode';
  const isEmail = field.type === 'email';

  useEffect(() => {
    setError('');
  }, [resetVersion]);

  const validate = (value) => {
    if (!value) {
      setError('');
      return true;
    }
    if (isMobile && !/^\d{10}$/.test(value)) {
      setError('Mobile number must contain exactly 10 digits.');
      return false;
    }
    if (isPincode && !/^\d{6}$/.test(value)) {
      setError('Pincode must contain exactly 6 digits.');
      return false;
    }
    if (isEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) {
      setError('Enter a valid email address.');
      return false;
    }
    setError('');
    return true;
  };

  const handleChange = (event) => {
    if (isMobile || isPincode) {
      event.target.value = event.target.value.replace(/\D/g, '').slice(0, isMobile ? 10 : 6);
    }
    validate(event.target.value);
  };

  return (
    <Grid item xs={field.xs || 12} sm={field.sm || 6}>
      <TextField
        fullWidth
        size="small"
        variant="outlined"
        name={field.name}
        label={
          field.required ? (
            <>
              {field.label} <Box component="span" sx={{ color: APP_RED }}>*</Box>
            </>
          ) : (
            field.label
          )
        }
        placeholder={field.placeholder}
        type={field.type || 'text'}
        error={Boolean(error)}
        helperText={error}
        onChange={handleChange}
        onBlur={(event) => validate(event.target.value)}
        inputProps={{
          ...field.inputProps,
          required: field.required || undefined,
          inputMode: isMobile || isPincode ? 'numeric' : undefined
        }}
        InputLabelProps={field.inputLabelProps}
      />
    </Grid>
  );
};

const SectionCard = ({ number, title, children }) => {
  return (
    <Card variant="outlined" sx={{ borderRadius: 2, borderColor: 'divider', overflow: 'visible' }}>
      <CardContent sx={{ p: { xs: 2, sm: 2.5 }, '&:last-child': { pb: { xs: 2, sm: 2.5 } } }}>
        <Stack direction="row" spacing={1.25} alignItems="center" mb={2.25}>
          <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: APP_RED, color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: 13 }}>{number}</Box>
          <Typography variant="h5" sx={{ color: APP_DARK, fontWeight: 600 }}>{title}</Typography>
        </Stack>
        {children}
      </CardContent>
    </Card>
  );
};

const NumberGroup = ({ title, fields }) => (
  <Box>
    <Typography variant="overline" sx={{ color: APP_RED, fontWeight: 700, letterSpacing: 1 }}>{title}</Typography>
    <Grid container spacing={2} mt={-0.5}>
      {fields.map(([name, label]) => (
        <Grid item xs={12} sm={fields.length > 2 ? 3 : 6} key={name}>
          <TextField fullWidth size="small" type="number" name={name} label={label} inputProps={{ min: 0 }} />
        </Grid>
      ))}
    </Grid>
  </Box>
);

const SalesPersonVisit = () => {
  const [showEntryForm, setShowEntryForm] = useState(false);
  const [tablePage, setTablePage] = useState(0);
  const [rowsPerPage] = useState(20);
  const [visitRows, setVisitRows] = useState([]);
  const [totalVisitRows, setTotalVisitRows] = useState(0);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [filters, setFilters] = useState({ retailer: '', fromDate: '', toDate: '' });
  const [photoToView, setPhotoToView] = useState(null);
  const formRef = useRef(null);
  const [photo, setPhoto] = useState(null);
  const [photoError, setPhotoError] = useState('');
  const [resetVersion, setResetVersion] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [coordinates, setCoordinates] = useState(null);
  const [salesPersonOptions, setSalesPersonOptions] = useState([]);
  const [selectedSalesPerson, setSelectedSalesPerson] = useState(null);
  const [salesPersonSearch, setSalesPersonSearch] = useState('');
  const [salesPersonLoading, setSalesPersonLoading] = useState(false);
  const [loggedInUserOption, setLoggedInUserOption] = useState(null);
  const [otherBrand, setOtherBrand] = useState(false);
  useEffect(() => {
    let active = true;
    const loadRows = async () => {
      setIsTableLoading(true);
      try {
        const response = await fetchSalesPersonVisitEntryReport({
          retailerName: filters.retailer,
          fromDate: filters.fromDate,
          toDate: filters.toDate,
          page: tablePage + 1,
          limit: rowsPerPage
        });
        if (!active) return;
        const data = Array.isArray(response?.data) ? response.data : [];
        setVisitRows(data.map((item) => ({
          id: item.visitEntryId,
          sales_person: item.salesPerson,
          location: item.location,
          pincode: item.pincode,
          retailer_name: item.retailerName,
          place: item.place,
          proprietor: item.proprietor,
          mobile: item.mobile,
          proprietor_email: item.proprietorEmail,
          retailer_email: item.retailerEmail,
          dob: item.proprietorDob,
          shop_area: item.shopArea,
          stock_total_shirting: item.stockTotal?.shirting,
          stock_total_suiting: item.stockTotal?.suiting,
          sale_daily: item.totalShopSale?.daily,
          sale_monthly: item.totalShopSale?.monthly,
          sale_yearly: item.totalShopSale?.yearly,
          sample_mill_dealer: item.sampleMillDealer,
          ssml_stock_shirting: item.ssmlBrandStockDisplayed?.shirting,
          ssml_stock_suiting: item.ssmlBrandStockDisplayed?.suiting,
          ssml_sale_shirting: item.ssmlBrandSale?.shirting,
          ssml_sale_suiting: item.ssmlBrandSale?.suiting,
          white_thaan_shop_shirting: item.whiteThaanShopStock?.shirting,
          white_thaan_shop_suiting: item.whiteThaanShopStock?.suiting,
          white_thaan_ssml_shirting: item.ssmlWhiteThaanStock?.shirting,
          white_thaan_ssml_suiting: item.ssmlWhiteThaanStock?.suiting,
          black_thaan_shop_shirting: item.blackThaanShopStock?.shirting,
          black_thaan_shop_suiting: item.blackThaanShopStock?.suiting,
          black_thaan_ssml_shirting: item.ssmlBlackThaanStock?.shirting,
          black_thaan_ssml_suiting: item.ssmlBlackThaanStock?.suiting,
          colour_solidness: item.colourSolidness,
          dealer_repo_feedback: item.dealerRepoFeedback,
          shop_photo: item.shopPhotoPath ? `${BASE_URL.replace(/api\/$/, '')}${item.shopPhotoPath}` : ''
        })));
        setTotalVisitRows(Number(response?.pagination?.totalRecords || 0));
      } catch (error) {
        if (active) { setVisitRows([]); setTotalVisitRows(0); }
      } finally {
        if (active) setIsTableLoading(false);
      }
    };
    loadRows();
    return () => { active = false; };
  }, [filters, rowsPerPage, tablePage]);
  const previewUrl = useMemo(() => (photo ? URL.createObjectURL(photo) : ''), [photo]);
  const loggedInUser = useMemo(() => {
    try {
      const storedUser = localStorage.getItem('user');
      const parsedUser = storedUser ? JSON.parse(storedUser) : null;
      const user = parsedUser && typeof parsedUser === 'object' ? parsedUser : {};
      return {
        userCode: String(user.userCode || ''),
        name: user.displayName || user.userName || ''
      };
    } catch (error) {
      return { userCode: '', name: '' };
    }
  }, []);

  useEffect(() => {
    let active = true;
    const timer = setTimeout(async () => {
      setSalesPersonLoading(true);
      try {
        const response = await fetchUsers(salesPersonSearch.trim());
        if (active) {
          const options = response?.success === true && Array.isArray(response.data)
            ? response.data.map((row) => {
              const code = String(row.userCode || '');
              const name = row.displayName || row.userName || '';
              return {
                value: code,
                label: name && name !== code ? `${name} (${code})` : (name || code),
                name
              };
            }).filter((option) => option.value)
            : [];
          setSalesPersonOptions(options);
          if (!salesPersonSearch.trim() && loggedInUser.userCode) {
            const currentUserOption = options.find((option) => option.value === loggedInUser.userCode) || (
              loggedInUser.name
                ? {
                  value: loggedInUser.userCode,
                  label: loggedInUser.name !== loggedInUser.userCode ? `${loggedInUser.name} (${loggedInUser.userCode})` : loggedInUser.name,
                  name: loggedInUser.name
                }
                : null
            );
            setLoggedInUserOption(currentUserOption);
            setSelectedSalesPerson(currentUserOption);
          }
        }
      } catch (error) {
        if (active) setSalesPersonOptions([]);
      } finally {
        if (active) setSalesPersonLoading(false);
      }
    }, 300);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [loggedInUser, salesPersonSearch]);

  const captureCurrentLocation = () => {
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        setCoordinates({
          latitude: coords.latitude.toFixed(7),
          longitude: coords.longitude.toFixed(7)
        });
      },
      () => {
        setCoordinates(null);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  useEffect(() => {
    captureCurrentLocation();
  }, []);

  const handleReset = () => {
    formRef.current?.reset();
    setPhoto(null);
    setPhotoError('');
    setCoordinates(null);
    setSelectedSalesPerson(loggedInUserOption);
    setSalesPersonSearch('');
    setOtherBrand(false);
    setResetVersion((version) => version + 1);
    captureCurrentLocation();
  };

  const handlePhotoChange = async (event) => {
    const file = event.target.files?.[0];
    setPhoto(null);
    setPhotoError('');

    if (!file) return;

    const nameParts = file.name.split('.');
    const extension = nameParts.length === 2 ? nameParts[1].toLowerCase() : '';
    const allowedExtensions = ['jpg', 'jpeg', 'png'];
    const allowedMimeTypes = ['image/jpeg', 'image/png'];

    if (nameParts.length !== 2 || !nameParts[0] || !allowedExtensions.includes(extension)) {
      event.target.value = '';
      setPhotoError('Only a single .jpg, .jpeg or .png extension is allowed. Double extensions are not permitted.');
      return;
    }

    if (!allowedMimeTypes.includes(file.type)) {
      event.target.value = '';
      setPhotoError('Only JPG, JPEG and PNG image files are allowed.');
      return;
    }

    const bytes = new Uint8Array(await file.slice(0, 8).arrayBuffer());
    const isJpeg = bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff;
    const isPng = bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47 && bytes[4] === 0x0d && bytes[5] === 0x0a && bytes[6] === 0x1a && bytes[7] === 0x0a;
    const signatureMatches = (file.type === 'image/jpeg' && isJpeg) || (file.type === 'image/png' && isPng);

    if (!signatureMatches) {
      event.target.value = '';
      setPhotoError('The selected file is not a valid JPG or PNG image.');
      return;
    }

    setPhoto(file);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    if (photoError) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation',
        text: photoError,
        confirmButtonText: 'OK',
      });
      return;
    }

    if (!selectedSalesPerson) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation',
        text: 'Please select SSML Secondary Sales Person.',
        confirmButtonText: 'OK',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData(form);
      const selectedBrands = formData.getAll('brand[]').filter((brand) => brand !== 'Other');
      const customBrand = String(formData.get('other_brand_input') || '').trim();
      if (customBrand) selectedBrands.push(customBrand);
      formData.delete('brand[]');
      formData.delete('other_brand_input');
      selectedBrands.forEach((brand) => formData.append('brand[]', brand));
      const salesPersonName = selectedSalesPerson.name || selectedSalesPerson.label || '';
      formData.delete('sales_person_select');
      formData.set('sales_person', `${salesPersonName} (${selectedSalesPerson.value || ''})`);
      if (coordinates) {
        formData.set('latitude', coordinates.latitude);
        formData.set('longitude', coordinates.longitude);
      }
      const response = await insertSalesPersonVisit(formData);
      if (response?.success === true || String(response?.success).toLowerCase() === 'true') {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: response?.msg || 'Visit entry saved successfully.',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e31e24'
        });
        handleReset();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: response?.msg || 'Unable to save the visit entry.',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e31e24'
        });
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error?.message || 'Unable to save the visit entry.',
        confirmButtonText: 'OK',
        confirmButtonColor: '#e31e24'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!showEntryForm) {
    return (
      <Box sx={{ maxWidth: 1400, mx: 'auto', pt: 8, pb: 5 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} mb={2}>
          <Typography variant="h3" sx={{ color: '#fff', fontWeight: 700 }}>Sales Person Entry for Visit</Typography>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setShowEntryForm(true)} sx={{ textTransform: 'none', width: 'fit-content', minWidth: 190, ml: { xs: 'auto', sm: 0 }, px: 1.75, py: 0.75, whiteSpace: 'nowrap', bgcolor: APP_RED, '&:hover': { bgcolor: '#bd171c' } }}>Add Sales Person Entry</Button>
        </Stack>
        <Card sx={{ mb: 2 }}><CardContent><Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}><TextField fullWidth size="small" label="Retailer" value={filters.retailer} onChange={(e) => { setTablePage(0); setFilters((p) => ({ ...p, retailer: e.target.value })); }} /></Grid>
          <Grid item xs={12} sm={5} md={3}><TextField fullWidth size="small" type="date" label="From Date" InputLabelProps={{ shrink: true }} value={filters.fromDate} onChange={(e) => { setTablePage(0); setFilters((p) => ({ ...p, fromDate: e.target.value })); }} /></Grid>
          <Grid item xs={12} sm={5} md={3}><TextField fullWidth size="small" type="date" label="To Date" InputLabelProps={{ shrink: true }} value={filters.toDate} onChange={(e) => { setTablePage(0); setFilters((p) => ({ ...p, toDate: e.target.value })); }} /></Grid>
          <Grid item xs={12} sm={2} md={2}><Button fullWidth variant="outlined" onClick={() => { setFilters({ retailer: '', fromDate: '', toDate: '' }); setTablePage(0); }}>Reset</Button></Grid>
        </Grid></CardContent></Card>
        <Card><TableContainer sx={{ overflowX: 'auto' }}><Table size="small" sx={{ minWidth: 3000 }}><TableHead>
          <TableRow sx={{ bgcolor: '#f6f6f6' }}>
            <TableCell rowSpan={2} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Sr. No.</TableCell>
            {visitTableGroups.map(({ label, fields }) => <TableCell key={label} colSpan={fields.length} rowSpan={fields.length === 1 ? 2 : undefined} align="center" sx={{ fontWeight: 700, whiteSpace: 'nowrap', borderLeft: '1px solid #ddd' }}>{label}</TableCell>)}
            <TableCell rowSpan={2} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Photo or Selfie of Retailer Shop</TableCell>
          </TableRow>
          <TableRow sx={{ bgcolor: '#fafafa' }}>{visitTableGroups.filter(({ fields }) => fields.length > 1).flatMap(({ fields }) => fields.map(([key, label]) => <TableCell key={key} align="center" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{label}</TableCell>))}</TableRow>
        </TableHead><TableBody>{isTableLoading ? <TableRow><TableCell colSpan={visitTableColumns.length + 2} align="center">Loading...</TableCell></TableRow> : visitRows.map((row, index) => <TableRow hover key={row.id}><TableCell>{tablePage * rowsPerPage + index + 1}</TableCell>{visitTableColumns.map(([key]) => <TableCell key={key}>{row[key] || '-'}</TableCell>)}<TableCell>{row.shop_photo ? <IconButton color="primary" onClick={() => setPhotoToView(row.shop_photo)} aria-label="View shop photo"><VisibilityOutlinedIcon /></IconButton> : '-'}</TableCell></TableRow>)}</TableBody></Table></TableContainer><TablePagination component="div" count={totalVisitRows} page={tablePage} rowsPerPage={rowsPerPage} rowsPerPageOptions={[]} onPageChange={(_, page) => setTablePage(page)} /></Card>
        {photoToView && <Box onClick={() => setPhotoToView(null)} sx={{ position: 'fixed', inset: 0, zIndex: 1400, bgcolor: 'rgba(0,0,0,.7)', display: 'grid', placeItems: 'center', p: 3 }}><Box component="img" src={photoToView} alt="Retailer shop" sx={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: 2 }} /></Box>}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        maxWidth: 1180,
        mx: 'auto',
        pt: 8,
        pb: 5,
        '& .MuiInputLabel-root.Mui-focused, & .MuiFormLabel-root.Mui-focused': { color: APP_RED },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: APP_RED },
        '& .MuiCheckbox-root.Mui-checked, & .MuiRadio-root.Mui-checked': { color: APP_RED }
      }}
    >
      <Box component="form" ref={formRef} onSubmit={handleSubmit} noValidate={false}>
        <Stack spacing={2.25}>
          <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={1} sx={{ minHeight: 42 }}>
            <IconButton
              aria-label="Back to table"
              onClick={() => setShowEntryForm(false)}
              sx={{
                color: '#fff',
                bgcolor: APP_RED,
                border: '1px solid rgba(255,255,255,.35)',
                borderRadius: 1.5,
                p: 0.75,
                '&:hover': { bgcolor: '#bd171c' }
              }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Typography variant="h3" align="center" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.2 }}>
             Add Sales Person Entry for Visit
            </Typography>
          </Stack>
          {sections.map((section, index) => (
            <SectionCard key={section.title} number={index + 1} title={section.title}>
              <Grid container spacing={2}>
                {section.fields.map((field) => field.name === 'sales_person' ? (
                  <Grid item xs={field.xs || 12} sm={field.sm || 6} key={field.name}>
                    <Select
                      name="sales_person_select"
                      value={selectedSalesPerson}
                      inputValue={salesPersonSearch}
                      onChange={(option, meta) => {
                        setSelectedSalesPerson(option);
                        if (meta.action === 'clear' || meta.action === 'select-option') {
                          setSalesPersonSearch('');
                        }
                      }}
                      onInputChange={(value, meta) => {
                        if (meta.action === 'input-change') setSalesPersonSearch(value);
                      }}
                      options={salesPersonOptions}
                      isDisabled
                      isSearchable={false}
                      isClearable={false}
                      isLoading={salesPersonLoading}
                      placeholder="Search Secondary Sales Person name or code"
                      noOptionsMessage={() => salesPersonLoading ? 'Loading...' : 'No sales person found'}
                      styles={{
                        control: (base) => ({ ...base, minHeight: 40 }),
                        menu: (base) => ({ ...base, zIndex: 1000 })
                      }}
                    />
                  </Grid>
                ) : <Field key={field.name} field={field} resetVersion={resetVersion} />)}
              </Grid>
            </SectionCard>
          ))}

          <SectionCard number={3} title="Total Stock & Shop Sale">
            <Stack spacing={2.5}>{stockGroups.slice(0, 2).map((group) => <NumberGroup key={group.title} {...group} />)}</Stack>
          </SectionCard>

          <SectionCard number={4} title="SSML Brand Stock & Sale">
            <Stack spacing={2.5}>
              <Box>
                <FormLabel sx={{ color: APP_DARK, fontSize: 13, fontWeight: 700 }}>Brands Displayed in the Shop</FormLabel>
                <Stack key={`brands-${resetVersion}`} direction="row" flexWrap="wrap" mt={1} sx={{ gap: 1 }}>
                  {['Siyaram', 'Vimal', 'Raymond', 'Mitwa', 'Ashvira'].map((brand) => <FormControlLabel key={brand} sx={brandOptionSx} control={<Checkbox size="small" name="brand[]" value={brand} />} label={brand} />)}
                  <FormControlLabel
                    sx={brandOptionSx}
                    control={(
                      <Checkbox
                        size="small"
                        name="brand[]"
                        value="Other"
                        checked={otherBrand}
                        onChange={(event) => setOtherBrand(event.target.checked)}
                      />
                    )}
                    label="Other"
                  />
                </Stack>
                {otherBrand && (
                  <TextField
                    fullWidth
                    required
                    size="small"
                    name="other_brand_input"
                    label="Other brand name"
                    placeholder="Enter brand name"
                    sx={{ mt: 1.5 }}
                  />
                )}
              </Box>
              {stockGroups.slice(2, 4).map((group) => <NumberGroup key={group.title} {...group} />)}
            </Stack>
          </SectionCard>

          <SectionCard number={5} title="Thaan Stock Details">
            <Stack spacing={2.5}>{stockGroups.slice(4).map((group) => <NumberGroup key={group.title} {...group} />)}</Stack>
          </SectionCard>

          <SectionCard number={6} title="Quality, Feedback & Shop Photo">
            <Grid container spacing={2.5}>
              {[['colour_solidness', 'Colour Solidness & Sample Cutting Done?'], ['dealer_repo_feedback', 'Dealer Repo Feedback Given?']].map(([name, label]) => (
                <Grid item xs={12} sm={6} key={name}>
                  <FormLabel sx={{ color: `${APP_DARK} !important`, fontSize: 13, fontWeight: 700 }}>{label}</FormLabel>
                  <RadioGroup key={`${name}-${resetVersion}`} row name={name}>
                    <FormControlLabel value="Yes" sx={{ '& .MuiFormControlLabel-label': { color: `${APP_DARK} !important` } }} control={<Radio size="small" icon={radioIcon} checkedIcon={radioCheckedIcon} />} label="Yes" />
                    <FormControlLabel value="No" sx={{ '& .MuiFormControlLabel-label': { color: `${APP_DARK} !important` } }} control={<Radio size="small" icon={radioIcon} checkedIcon={radioCheckedIcon} />} label="No" />
                  </RadioGroup>
                </Grid>
              ))}
              <Grid item xs={12}><TextField fullWidth multiline minRows={3} name="feedback_notes" label="Feedback / Remarks" placeholder="Additional notes, complaints, or observations" /></Grid>
              <Grid item xs={12}>
                <Button component="label" fullWidth variant="outlined" startIcon={<CameraAltOutlinedIcon />} sx={{ minHeight: 86, borderStyle: 'dashed', color: APP_RED, borderColor: photoError ? 'error.main' : APP_RED, bgcolor: alpha(APP_RED, 0.025), '&:hover': { borderColor: APP_RED, bgcolor: alpha(APP_RED, 0.06) } }}>
                  {photo ? photo.name : 'Upload retailer shop photo'}
                  <input hidden type="file" name="shop_photo" accept=".jpg,.jpeg,.png,image/jpeg,image/png" onChange={handlePhotoChange} />
                </Button>
                {previewUrl && <Box component="img" src={previewUrl} alt="Shop preview" sx={{ display: 'block', mt: 1.5, width: 220, maxWidth: '100%', maxHeight: 180, objectFit: 'cover', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }} />}
                <Typography variant="caption" display="block" color={photoError ? 'error' : 'text.secondary'} sx={{ mt: 0.5 }}>
                  {photoError || 'Accepted formats: JPG, PNG. Photo is stored with this submission only.'}
                </Typography>
              </Grid>
            </Grid>
          </SectionCard>

          <Stack direction={{ xs: 'column-reverse', sm: 'row' }} justifyContent="flex-end" spacing={1.5}>
            <Button variant="outlined" color="inherit" startIcon={<DeleteOutlineIcon />} onClick={handleReset}>Clear Form</Button>
            <Button type="submit" variant="contained" disabled={isSubmitting} startIcon={<SaveOutlinedIcon />} sx={{ bgcolor: APP_RED, '&:hover': { bgcolor: '#bd171c' } }}>{isSubmitting ? 'Saving...' : 'Save Entry'}</Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default SalesPersonVisit;
